import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  EventDataMessageType,
  MessageReaction,
  MessageTypes,
  introMessage,
} from "@app/pippa/models";
import { APIContext } from "@app/pippa/provider";
import {
  SessionContext,
  getPassedOrSkippedFlowComponentsIdFromStorage,
  addPassedOrSkippedFlowComponentIdToStorage,
} from "@app/pippa/session";
import { ChatStreamChunk } from "@app/pippa/hooks/useApiController";

type EventsProviderState = {
  messages: EventDataMessageType[];
  streamingMessage: EventDataMessageType | null;
  showTyping: boolean;
  passedOrSkippedFlowComponentsId: string[];
  isInputDisabled: boolean;
  isInputHidden?: boolean;
};

function applyChunkToStreamingState(
  prev: EventsProviderState,
  chunk: ChatStreamChunk
): EventsProviderState {
  if (!prev.streamingMessage) {
    return prev;
  }

  if (chunk.delta?.content) {
    return {
      ...prev,
      streamingMessage: {
        ...prev.streamingMessage,
        text: prev.streamingMessage.text + chunk.delta.content,
      },
      showTyping: false,
    };
  }

  if (chunk.quickReplies) {
    return {
      ...prev,
      streamingMessage: {
        ...prev.streamingMessage,
        quickReplies: chunk.quickReplies,
      },
    };
  }

  if (chunk.feedback) {
    return {
      ...prev,
      streamingMessage: {
        ...prev.streamingMessage,
        feedback: chunk.feedback,
      },
    };
  }

  if (chunk.messageId) {
    return {
      ...prev,
      streamingMessage: {
        ...prev.streamingMessage,
        id: chunk.messageId,
      },
    };
  }

  if (chunk.feedbackEligible) {
    return {
      ...prev,
      streamingMessage: {
        ...prev.streamingMessage,
        feedbackEligible: true,
      },
    };
  }

  if (chunk.isInputDisabled !== undefined) {
    return {
      ...prev,
      isInputDisabled: chunk.isInputDisabled,
    };
  }
  return prev;
}

function finalizeStreamingState(
  prev: EventsProviderState
): EventsProviderState {
  if (!prev.streamingMessage) {
    return { ...prev, showTyping: false };
  }

  const finalMessage = {
    ...prev.streamingMessage,
    text: prev.streamingMessage.text.trim(),
    quickReplies: prev.streamingMessage.quickReplies,
    feedback: prev.streamingMessage.feedback,
    feedbackEligible: prev.streamingMessage.feedbackEligible,
  };

  return {
    ...prev,
    messages: [...prev.messages, finalMessage],
    streamingMessage: null,
    showTyping: false,
  };
}

export interface EventController {
  eventsProviderState: EventsProviderState;
  textMessageRef: React.RefObject<TextMessageRef> | null;
  quickReplyRef: React.RefObject<QuickReplyRef> | null;
  userInputRef: React.RefObject<UserInputRef> | null;
  userEventHandler: (message: string, chatId: string) => void;
  setShowTyping: (value: boolean) => void;
  setPassedOrSkippedFlowComponentsId: (passedComponentId: string) => void;
  addMessages: (messages: EventDataMessageType[]) => void;
  setIsInputDisabled: (value: boolean) => void;
  replaceMessages: (messages: EventDataMessageType[]) => void;
  setIsInputHidden: (value: boolean) => void;
  setMessageReaction: (
    messageId: string,
    reaction: MessageReaction | undefined
  ) => void;
}

export interface TextMessageRef {
  disableRating: () => void;
}

export interface QuickReplyRef {
  removeQuickReplies: () => void;
}

export interface UserInputRef {
  clearInput: () => void;
}

export const Context = createContext<EventController>({
  eventsProviderState: {
    messages: [],
    streamingMessage: null,
    showTyping: false,
    passedOrSkippedFlowComponentsId: [],
    isInputDisabled: false,
    isInputHidden: false,
  },
  textMessageRef: null,
  quickReplyRef: null,
  userInputRef: null,
  userEventHandler: () => undefined,
  setShowTyping: () => undefined,
  setPassedOrSkippedFlowComponentsId: () => undefined,
  addMessages: () => undefined,
  setIsInputDisabled: () => undefined,
  replaceMessages: () => undefined,
  setIsInputHidden: () => undefined,
  setMessageReaction: () => undefined,
});

Context.displayName = "EventsContext";

const EventsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { sendUserMessage } = useContext(APIContext);
  const { updateSessionActivity } = useContext(SessionContext);
  const [eventsProviderState, setEventsProviderState] =
    useState<EventsProviderState>(() => ({
      messages: [introMessage],
      streamingMessage: null,
      showTyping: false,
      passedOrSkippedFlowComponentsId:
        getPassedOrSkippedFlowComponentsIdFromStorage(),
      isInputDisabled: false,
      isInputHidden: false,
    }));
  const textMessageRef = useRef<TextMessageRef>(
    null
  ) as React.RefObject<TextMessageRef>;
  const quickReplyRef = useRef<QuickReplyRef>(
    null
  ) as React.RefObject<QuickReplyRef>;
  const userInputRef = useRef<UserInputRef>(
    null
  ) as React.RefObject<UserInputRef>;

  const processMessages = (message: EventDataMessageType) => {
    setEventsProviderState((prevState) => ({
      ...prevState,
      messages: [...prevState.messages, message],
    }));
  };

  const userEventHandler = (message: string, chatId: string) => {
    const preparedMessage = {
      id: uuidv4(),
      type: MessageTypes.TEXT,
      text: message,
      isSendByUser: true,
    };
    processMessages(preparedMessage);
    updateSessionActivity(chatId);

    sendUserMessage(message, chatId, {
      onStart: () => {
        setEventsProviderState((prev) => ({
          ...prev,
          streamingMessage: {
            id: uuidv4(),
            type: MessageTypes.TEXT,
            text: "",
            quickReplies: undefined,
            feedback: undefined,
            feedbackEligible: undefined,
          },
          showTyping: true,
        }));
      },
      onChunk: (chunk: ChatStreamChunk) => {
        setEventsProviderState((prev) =>
          applyChunkToStreamingState(prev, chunk)
        );
      },
      onDone: () => {
        setEventsProviderState(finalizeStreamingState);
        updateSessionActivity(chatId);
      },
    });
  };

  const setPassedOrSkippedFlowComponentsId = (passedComponentId: string) => {
    addPassedOrSkippedFlowComponentIdToStorage(passedComponentId);
    setEventsProviderState((prevState) => ({
      ...prevState,
      passedOrSkippedFlowComponentsId: [
        ...prevState.passedOrSkippedFlowComponentsId,
        passedComponentId,
      ],
    }));
  };

  const setShowTyping = (value: boolean) => {
    setEventsProviderState((prevState) => ({
      ...prevState,
      showTyping: value,
    }));
  };

  const addMessages = (messages: EventDataMessageType[]) => {
    setEventsProviderState((prevState) => ({
      ...prevState,
      messages: [...prevState.messages, ...messages],
    }));
  };

  const setIsInputDisabled = (value: boolean) => {
    setEventsProviderState((prevState) => ({
      ...prevState,
      isInputDisabled: value,
    }));
  };

  const setIsInputHidden = (value: boolean) => {
    setEventsProviderState((prevState) => ({
      ...prevState,
      isInputHidden: value,
    }));
  };

  // Reaction lives on the message itself, so `replaceMessages` from history restores it
  // for free — no separate map to merge back in.
  const setMessageReaction = (
    messageId: string,
    reaction: MessageReaction | undefined
  ) => {
    setEventsProviderState((prevState) => ({
      ...prevState,
      messages: prevState.messages.map((message) =>
        message.id === messageId ? { ...message, reaction } : message
      ),
    }));
  };

  const replaceMessages = (messages: EventDataMessageType[]) => {
    setEventsProviderState((prevState) => ({
      ...prevState,
      messages,
      passedOrSkippedFlowComponentsId:
        getPassedOrSkippedFlowComponentsIdFromStorage(),
    }));
  };

  const value = useMemo(
    () => ({
      eventsProviderState,
      userEventHandler,
      setPassedOrSkippedFlowComponentsId,
      setShowTyping,
      textMessageRef,
      quickReplyRef,
      userInputRef,
      addMessages,
      replaceMessages,
      setIsInputDisabled,
      setIsInputHidden,
      setMessageReaction,
    }),
    [eventsProviderState]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default EventsProvider;
